import { SynodPaper, SynodPapers, SynodPaperUploadTicket } from "../data/SynodPapers";
import { ApiResponse, ApiStatus, callApi } from "./ApiUtilities";

function dataOrThrow<T>(response: ApiResponse<T>): T {
  if (response.status === ApiStatus.Ok) {
    return response.data as T;
  } else if (response.status === ApiStatus.Unauthorised) {
    throw Error(`Not logged in, try refreshing page (${response.message}`);
  }
  throw Error(response.message);
}

/** The home page block: note plus visible papers. */
export async function getPapers(): Promise<SynodPapers> {
  return dataOrThrow(await callApi<SynodPapers>("/api/papers", "GET"));
}

/** Admin: note plus all papers, including hidden ones. */
export async function getAllPapers(): Promise<SynodPapers> {
  return dataOrThrow(await callApi<SynodPapers>("/api/papers/all", "GET"));
}

export async function savePapers(papers: SynodPapers): Promise<SynodPapers> {
  const update = {
    contentMarkdown: papers.contentMarkdown,
    papers: papers.papers.map((p) => ({ id: p.id, title: p.title, visible: p.visible })),
  };
  return dataOrThrow(await callApi<SynodPapers>("/api/papers", "POST", update));
}

export async function deletePaper(id: string): Promise<void> {
  dataOrThrow(await callApi<string>(`/api/papers/${encodeURIComponent(id)}`, "DELETE"));
}

/**
 * Uploads a PDF in three steps: get a pre-signed S3 URL from the API, PUT the
 * file straight to S3 (avoids the API's request size limit), then ask the API
 * to check the file and add it to the list.
 */
export async function uploadPaper(file: File, title: string): Promise<SynodPaper> {
  const ticket = dataOrThrow(await callApi<SynodPaperUploadTicket>("/api/papers/upload", "POST", { title, fileName: file.name }));

  let s3Response: Response;
  try {
    s3Response = await fetch(ticket.uploadUrl, { method: "PUT", headers: { "content-type": "application/pdf" }, body: file });
  } catch (error: any) {
    throw Error(`Upload to storage failed: ${error.message}`);
  }
  if (!s3Response.ok) {
    throw Error(`Upload to storage failed (HTTP ${s3Response.status})`);
  }

  return dataOrThrow(
    await callApi<SynodPaper>(`/api/papers/upload/${encodeURIComponent(ticket.id)}/complete`, "POST", { title, fileName: ticket.fileName })
  );
}
